/**
 * Application Received — gated tenant signup confirmation.
 *
 * Shown after a self-serve tenant submission when the sysadmin tenant-approval
 * gate is on: the tenant + user are held in `pending_approval` and NO auth
 * tokens are issued, so instead of dropping the user into an active app we land
 * them here. Reused by both setup wizards (v1 + v2).
 */

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../shared/auth';
import { STANDALONE_TITLE } from '../../shared/styles/typography';

interface ApplicationReceivedScreenProps {
  /** Email the applicant signed up with — echoed back as the notification target. */
  email: string;
  /** Workspace name the applicant requested, shown for reassurance. */
  companyName?: string | null;
}

export function ApplicationReceivedScreen({ email, companyName }: ApplicationReceivedScreenProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleBackToSignIn = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 py-8">
      <Card className="w-full max-w-[600px] shadow-lg">
        <CardContent className="space-y-6 p-6 text-center sm:p-8">
          <div className="flex justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-info/10 text-info">
              <MailCheck className="h-8 w-8" />
            </span>
          </div>

          <div className="space-y-2">
            <h1 className={STANDALONE_TITLE}>
              {t('setupWizard.applicationReceived.title')}
            </h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              {companyName
                ? t('setupWizard.applicationReceived.subtitleNamed', { name: companyName })
                : t('setupWizard.applicationReceived.subtitle')}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="setupWizard.applicationReceived.notifyHint"
              values={{ email }}
              components={{ strong: <span className="font-medium text-foreground" /> }}
            />
          </div>

          <Button className="h-11 w-full" onClick={handleBackToSignIn}>
            {t('setupWizard.applicationReceived.backToSignIn')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
